import Mailcheck from 'mailcheck';
import { type SafeParseReturnType } from 'zod';

import {
  type FormSchemaType,
  type getValidator,
  type VisibleErrors,
  type VisibleFieldType,
} from '@/components/registrationForm/config';
import { isDefined } from '@/utils/isDefined';

const domains = [
  'msn.com',
  'bellsouth.net',
  'telus.net',
  'comcast.net',
  'optusnet.com.au',
  'earthlink.net',
  'qq.com',
  'sky.com',
  'icloud.com',
  'mac.com',
  'sympatico.ca',
  'googlemail.com',
  'att.net',
  'xtra.co.nz',
  'web.de',
  'cox.net',
  'gmail.com',
  'ymail.com',
  'aim.com',
  'rogers.com',
  'verizon.net',
  'rocketmail.com',
  'google.com',
  'optonline.net',
  'sbcglobal.net',
  'aol.com',
  'me.com',
  'btinternet.com',
  'charter.net',
  'shaw.ca',
];

const secondLevelDomains = [
  'yahoo',
  'hotmail',
  'gmail',
  'mail',
  'live',
  'outlook',
  'gmx',
];

interface EmailSuggestion {
  address: string;
  domain: string;
  full: string;
}

const validateEmailSuggestion = (
  suggestion: EmailSuggestion,
  validator: ReturnType<typeof getValidator>,
) => {
  const validation = validator.safeParse({ email: suggestion.full });
  return validation.success || !validation.error.format().email;
};

export const getEmailSuggestion = (
  email: string,
  validator: ReturnType<typeof getValidator>,
): EmailSuggestion | null => {
  let emailSuggestion: EmailSuggestion = { address: '', domain: '', full: '' };

  Mailcheck.run({
    domains,
    secondLevelDomains,
    email,
    suggested: (suggestion: EmailSuggestion) => {
      emailSuggestion = suggestion;
    },
    empty: () => {
      // this block runs when no typo is detected
    },
  });
  const hasValidSuggestion =
    Object.values(emailSuggestion).some((value: string) => value.length > 0) &&
    validateEmailSuggestion(emailSuggestion, validator);

  return hasValidSuggestion ? emailSuggestion : null;
};

export const getVisibleErrors = (
  visibleFields: VisibleFieldType[],
  getMessage: (key: VisibleFieldType) => string | string[] | undefined,
  first = false,
): VisibleErrors =>
  visibleFields.reduce((errors, key) => {
    if (first && Object.keys(errors).length) {
      return errors;
    }
    const message = getMessage(key);
    if (isDefined(message)) {
      errors[key] = Array.isArray(message) ? message[0] : message;
    }
    return errors;
  }, {} as VisibleErrors);

export const mapToFormErrorMessages = (
  validator: SafeParseReturnType<FormSchemaType, FormSchemaType>,
  visibleFields: VisibleFieldType[],
): VisibleErrors => {
  if (validator.success) {
    return {} as VisibleErrors;
  }
  const formattedErrors = validator.error.format();
  return getVisibleErrors(
    visibleFields,
    (key: VisibleFieldType) => formattedErrors[key]?._errors[0],
  );
};

export const mapToSingleError = (
  visibleFields: VisibleFieldType[],
  getMessage: (key: VisibleFieldType) => string | string[] | undefined,
): VisibleErrors => {
  const errors = getVisibleErrors(visibleFields, getMessage);
  const errorEntries = Object.entries(
    getVisibleErrors(visibleFields, getMessage, true),
  );
  if (!errorEntries.length) {
    return {} as VisibleErrors;
  }
  const [[key, message]] = errorEntries;
  const count = Object.keys(errors).length;
  const append = ` (and ${count - 1} more error${count > 2 ? 's' : ''})`;

  return { [key]: message + (count > 1 ? append : '') } as VisibleErrors;
};

export const focusElementByName = (name?: VisibleFieldType) => {
  if (name) {
    document.getElementsByName(name)[0]?.focus();
  }
};

export const focusElementToFirstError = (
  errors: VisibleErrors,
  visibleFields: VisibleFieldType[],
): void => {
  const element = visibleFields.reduce(
    (name, field) => {
      return !isDefined(name) && errors[field] ? field : name;
    },
    undefined as VisibleFieldType | undefined,
  );
  focusElementByName(element);
};
